import CalendarPresenter from '@src/calendar/calendarPresenter';
import CalendarSelector from '@src/calendar/components/calendarSelector';
import CartModalPresenter from '@src/cart/cartModalPresenter';
import TwoColumnCheckoutBody from '@src/checkout/components/TwoColumnCheckoutBody';
import { getUser } from '@src/services/auth';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'theme-ui';
import BookALesson from './bookALesson';
import CheckoutLessonPresenter from './checkoutLessonPresenter';

const checkoutLesson = () => {
  const { t, i18n } = useTranslation();
  const user = getUser();

  const checkoutLessonPresenter = new CheckoutLessonPresenter();
  const cartModalPresenter = new CartModalPresenter();
  const calendarPresenter = new CalendarPresenter();
  const [viewModel, setViewModel] = useState({});
  const [calendar, setCalendar] = useState();
  const [teacher, setTeacher] = useState(null);
  const [teacherProduct, setTeacherProduct] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [lessonCount, setLessonCount] = useState(1);
  const [showSelectedDates, setShowSelectedDates] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(0);
  const [priceWithDiscount, setPriceWithDiscount] = useState(0);

  const discountOptions = [
    {
      count: 1,
      discount: 0,
      bigBoxBackground: '#F4F4F4',
      smallBoxBackground: '#EBEBEB',
    },
    {
      count: 4,
      discount: 5,
      bigBoxBackground: '#FCF4D8',
      smallBoxBackground:
        'transparent linear-gradient(0deg, #FFFFFF 0%, #FBEAAF 100%)',
    },
    {
      count: 8,
      discount: 10,
      bigBoxBackground: '#DCF2E6',
      smallBoxBackground:
        'transparent linear-gradient(0deg, #FFFFFF 0%, #94e0b4 100%)',
      message: t('mostPopular'),
    },
    {
      count: 16,
      discount: 15,
      bigBoxBackground: '#EEEBFF',
      smallBoxBackground:
        'transparent linear-gradient(0deg, #FFFFFF 0%, #C8BEFF 100%)',
    },
  ];
  useEffect(() => {
    const updateView = (generatedVm) => {
      setViewModel(generatedVm);
    };

    checkoutLessonPresenter.load(updateView);

    return updateView;
  }, []);

  useEffect(() => {
    const updateCalendarView = (generatedVm) => {
      setCalendar(generatedVm);
    };

    if (viewModel.id) {
      calendarPresenter.initCalendar(updateCalendarView, viewModel.id);
    }

    return updateCalendarView;
  }, [viewModel.id]);
  const bookALessonViewModel = {
    teacherAvatarUrl: viewModel.teacherAvatarUrl,
    teacherName: viewModel.teacherName,
    teacherSubjects: viewModel.teacherSubjects?.map((subject) => {
      return {
        value: subject.name,
        label: subject.name,
        grades: subject.grades,
      };
    }),

    lessonDay: selectedTimes.length
      ? new Date(
          selectedTimes[selectedTimes.length - 1]?.date
        ).toLocaleDateString(i18n.language, {
          month: 'numeric',
        }) +
        `.` +
        new Date(
          selectedTimes[selectedTimes.length - 1]?.date
        ).toLocaleDateString(i18n.language, {
          day: 'numeric',
        })
      : t('dateNotSelected'),
    lessonTime:
      selectedTimes.length > 0 &&
      new Date(
        selectedTimes[selectedTimes.length - 1]?.date
      ).toLocaleDateString(i18n.language, {
        weekday: 'long',
      }) + `, ${selectedTimes[selectedTimes.length - 1]?.hour}.00`, // 'pirmadienis, 8.00',
    hasLessonTypes: viewModel.hasLessonTypes,
    lessonTypes: viewModel.lessonTypes,
    lessonStudentCount: viewModel.lessonStudentCount,
    lessonMax: viewModel.lessonMax,
    lessonForUsers: user.role == 4 ? user.childrenData : [],
    price: viewModel.price,
    currency: viewModel.currency,
  };

  return (
    <Box sx={{ width: '1200px', maxWidth: '90vw' }}>
      <Box
        className="checkout-header"
        sx={{
          padding: ['30px 15px', '30px'],
          textAlign: 'center',
          fontSize: '28px',
        }}
      >
        <Box sx={{ margin: 0 }}>{viewModel.title}</Box>
      </Box>
      <TwoColumnCheckoutBody
        firstColumn={
          <BookALesson
            vm={bookALessonViewModel}
            isDisabled={!checkoutLessonPresenter.getSelectedTime()}
            onSelect={({
              day,
              time,
              lessonType,
              lessonStudentCount,
              lessonMax,
            }) => {
              checkoutLessonPresenter.selectLessonTime({
                day,
                time,
                lessonType,
                lessonStudentCount,
                lessonMax,
              });
            }}
            addToCart={async ({
              selectedProductId,
              topic,
              subject,
              grade,
              forUser,
              loading,
              timeAlreadyInCart,
            }) => {
              if (!selectedProductId || !topic || !subject) {
                loading(false);
                return;
              }
              const addedItem = await cartModalPresenter.addItemToCart({
                variantId: selectedProductId,
                topic,
                selectedTime: checkoutLessonPresenter.getSelectedTime(),
                user,
                subject,
                grade,
                forUser,
              });

              timeAlreadyInCart(!addedItem);
            }}
            lessonCount={lessonCount}
            setLessonCount={setLessonCount}
            setShowCalendar={setShowCalendar}
            setShowSelectedDates={setShowSelectedDates}
            selectedTimes={selectedTimes}
            selectedDiscount={selectedDiscount}
            setSelectedDiscount={setSelectedDiscount}
            priceWithDiscount={priceWithDiscount}
            setPriceWithDiscount={setPriceWithDiscount}
            discountOptions={discountOptions}
            cartModalPresenter={cartModalPresenter}
            onNext={async ({
              selectedProductId,
              topic,
              subject,
              grade,
              forUser,
              loading,
              timeAlreadyInCart,
            }) => {
              if (!selectedProductId || !topic || !subject) {
                loading(false);
                return;
              }

              const addedItem = await cartModalPresenter.addItemToCart({
                variantId: selectedProductId,
                topic,
                selectedTime: checkoutLessonPresenter.getSelectedTime(),
                user,
                subject,
                grade,
                forUser,
              });
              timeAlreadyInCart(!addedItem);
            }}
          />
        }
        secondColumn={
          <CalendarSelector
            isLoading={calendar?.isLoading}
            onSelect={({
              day,
              time,
              lessonType,
              lessonStudentCount,
              lessonMax,
            }) => {
              checkoutLessonPresenter.selectLessonTime({
                day,
                time,
                lessonType,
                lessonStudentCount,
                lessonMax,
              });
            }}
            calendarData={
              calendar?.calendarData?.length && calendar.calendarData
            }
            onPrev={calendarPresenter.prevWeek}
            onNext={calendarPresenter.nextWeek}
            showCalendar={showCalendar}
            lessonCount={lessonCount}
            showSelectedDates={showSelectedDates}
            setShowSelectedDates={setShowSelectedDates}
            selectedTimes={selectedTimes}
            setSelectedTimes={setSelectedTimes}
            price={viewModel.price}
            currency={viewModel.currency}
            selectedDiscount={selectedDiscount}
            priceWithDiscount={priceWithDiscount}
            discountOptions={discountOptions}
            teacherSubjects={viewModel.teacherSubjects}
          />
        }
      />
    </Box>
  );
};

export default checkoutLesson;
